import { registerPlugin } from "@capacitor/core";
import { isLargeOrRebuildableLocalStorageKey, recoverLocalStorageQuota, safeLocalStorageSetItem } from "./storageQuotaRecovery";
// Device ID único persistido localmente.
// v2.225.06: no app nativo iOS, a identidade canônica vem do Keychain via bridge Capacitor.
// A versão síncrona continua existindo para Browser/PWA e para chamadas auxiliares antigas.
let _nativeIdentityPlugin = null;
let _nativeDeviceIdPromise = null;

function generateDeviceId() {
  return "dev_" + Math.random().toString(36).slice(2) + Date.now();
}

function isNativeCapacitorRuntime() {
  try {
    const cap = window?.Capacitor;
    if (cap?.isNativePlatform?.()) return true;
    return /Capacitor|TremboNative|fazenda-hostgator/i.test(String(window?.navigator?.userAgent || ""));
  } catch (_) {
    return false;
  }
}

function getNativeIdentityPlugin() {
  if (_nativeIdentityPlugin) return _nativeIdentityPlugin;
  try {
    _nativeIdentityPlugin = registerPlugin("TremboLiveActivities");
  } catch (_) {
    _nativeIdentityPlugin = null;
  }
  return _nativeIdentityPlugin;
}

export function getDeviceId() {
  let id = "";
  try { id = localStorage.getItem("fz:device_id") || ""; } catch (_) { id = ""; }
  if (!id) {
    id = generateDeviceId();
    safeLocalStorageSetItem("fz:device_id", id, { silent: true });
  }
  return id;
}

export async function getDeviceIdAsync() {
  if (!isNativeCapacitorRuntime()) return getDeviceId();
  if (_nativeDeviceIdPromise) return _nativeDeviceIdPromise;
  _nativeDeviceIdPromise = (async () => {
    const fallbackId = getDeviceId();
    try {
      const plugin = getNativeIdentityPlugin();
      if (plugin?.getCanonicalDeviceId) {
        const result = await plugin.getCanonicalDeviceId({ fallbackDeviceId: fallbackId });
        const nativeId = String(result?.deviceId || "").trim();
        if (nativeId) {
          safeLocalStorageSetItem("fz:device_id", nativeId, { silent: true });
          return nativeId;
        }
      }
    } catch (_) {}
    return fallbackId;
  })();
  try {
    return await _nativeDeviceIdPromise;
  } finally {
    _nativeDeviceIdPromise = null;
  }
}

const FZ_IDB_NAME = "fazenda-hostgator-offline";
const FZ_IDB_VERSION = 1;
const FZ_IDB_STORE = "kv";
let _idbPromise = null;

function openAppDb() {
  if (_idbPromise) return _idbPromise;
  _idbPromise = new Promise((resolve) => {
    try {
      if (!("indexedDB" in window)) {
        resolve(null);
        return;
      }
      const req = indexedDB.open(FZ_IDB_NAME, FZ_IDB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(FZ_IDB_STORE)) {
          db.createObjectStore(FZ_IDB_STORE, { keyPath: "key" });
        }
      };
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch (e) {
      resolve(null);
    }
  });
  return _idbPromise;
}

async function idbGet(key) {
  const db = await openAppDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(FZ_IDB_STORE, "readonly");
      const store = tx.objectStore(FZ_IDB_STORE);
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result ? req.result.value : null);
      req.onerror = () => resolve(null);
    } catch (e) {
      resolve(null);
    }
  });
}

async function idbSet(key, value) {
  const db = await openAppDb();
  if (!db) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(FZ_IDB_STORE, "readwrite");
      const store = tx.objectStore(FZ_IDB_STORE);
      store.put({ key, value, updatedAt: new Date().toISOString() });
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch (e) {
      resolve(false);
    }
  });
}

async function idbDelete(key) {
  const db = await openAppDb();
  if (!db) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(FZ_IDB_STORE, "readwrite");
      const store = tx.objectStore(FZ_IDB_STORE);
      store.delete(key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch (e) {
      resolve(false);
    }
  });
}

export const DB = {
  async get(k) {
    try {
      const fromIdb = await idbGet(k);
      if (fromIdb !== null && fromIdb !== undefined) return fromIdb;
    } catch {}
    try {
      const raw = localStorage.getItem(k);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed !== null) {
        idbSet(k, parsed).catch(() => {});
      }
      return parsed;
    } catch {
      return null;
    }
  },
  async set(k, v) {
    try {
      // v2.220.04: no app nativo/iOS, coleções grandes devem ficar no IndexedDB.
      // Não escrever sombras/coleções grandes no localStorage evita QuotaExceededError
      // em handleLogin e preserva sessão/device.
      if (!isLargeOrRebuildableLocalStorageKey(k)) {
        const raw = JSON.stringify(v);
        const ok = safeLocalStorageSetItem(k, raw, { silent: true });
        if (!ok && raw.length > 25000) recoverLocalStorageQuota(`DB.set:${k}`);
      } else {
        try { localStorage.removeItem(k); } catch (_) {}
      }
    } catch (e) {
      // v2.212.05: em iOS/WKWebView é comum localStorage falhar por quota
      // em coleções grandes. O IndexedDB continua sendo a fonte principal;
      // não usar console.error aqui para não inundar o Xcode em loops de sync.
      try {
        const key = `fz:storage-warning:local:${String(k || '').split(':').slice(0, 4).join(':')}`;
        if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          console.warn(`[storage] localStorage ignorado para ${k}; usando IndexedDB. ${e?.name || ''}`);
        }
      } catch (_) {}
    }
    try {
      await idbSet(k, v);
    } catch (e) {
      try {
        const key = `fz:storage-warning:idb:${String(k || '').split(':').slice(0, 4).join(':')}`;
        if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          console.warn(`[storage] IndexedDB falhou para ${k}. ${e?.name || ''}`);
        }
      } catch (_) {}
    }
  },
  async del(k) {
    try { localStorage.removeItem(k); } catch (_) {}
    try { await idbDelete(k); } catch (_) {}
  },
};
